import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { GridPageLayout } from '@lux/grid';
import { InlineAlert } from '@lux/inline-alert';
import { Tag, TitlePreset } from '@lux/title';
import { FlashMessage } from '@pegasus/ui-flashmessages';
import { Translate, TranslateScope } from '@pegasus/translation';
import { UpcomingProvider } from '@pegasus/upcoming-section';
import { BottomBubble } from '@browsing/components';
import { useConfig } from '@browsing/config';
import { useQuery } from '@browsing/hooks';
import { EarlyAccessPhase } from '@browsing/plus';

import { RECO_ITEM_WIDTH, TRACK_RECO_CAMPAIGN_OVERVIEW } from '@client/config/constants';
import { isXsScreen } from '@client/helpers/screens';
import { RootState } from '@client/redux/types';
import { getClientDeviceWidth } from '@client/redux/modules/clientDevice/selectors';
import { getCustomer, State as CustomerState } from '@client/redux/modules/customer';
import {
    getCategoryTabId,
    getOpenCampaigns,
    getTabs,
    makeGetUpcomingCampaigns,
    hasEarlyAccessCampaigns as getHasEarlyAccessCampaigns,
    getShowstoppers,
} from '@client/redux/modules/mylounge';
import { useRecommendations } from '@client/components/mylounge/hooks';
import { incrementPageVisitsCount } from '@client/helpers';

import { getTrackingData } from '@client/redux/modules/tracking/selectors';
import { useTrackCampaignsImpression } from '@browsing/tracking';
import { getIsMobileScreen } from '@client/redux/modules/clientDevice/selectors';
import { useVariant } from '@client/lib/assignments';
import { track } from '@client/helpers/tracking';

import { Campaign } from '@shared/types/mylounge';
import { convertRecommendationsToLegacy } from '../shared/recommendations/helpers';
import { AccessibleHeader } from '../shared/accessible-header';
import Banner from './banner/banner';
import { CategoryTabIds } from './banner/constants';
import Grid from './campaigns/grid';
import MobileCampaigns from './campaigns/mobileCampaigns/mobile-campaigns';
import UpcomingSection from './campaigns/upcoming/upcoming-section-wrapper';
import { convertFlashMessageToAlertType } from './helpers';
import { CAMPAIGN_WRAPPER, UPCOMING_CAMPAIGNS_WRAPPER } from './constants';
import { CampaignExpiredStatus, ExpiredCampaignDialog } from './expired-campaign-dialog';
import RecommendationsWrapper from './recommendations-wrapper';
import { EarlyAccessSection, PlusWelcomeDialog, useZalandoPlus } from './plus';
import { CrossCampaignEntry } from './cross-campaign';
import { CampaignFilters } from './campaign-filters';

import * as S from './mylounge-wrapper.styles';
import { buildShowstopperContent } from './campaigns/components/showstopper';

type FlashMessageWithText = FlashMessage & {
    text: string;
};

interface Props {
    flashMessages?: FlashMessage[];
}

const EA_UPCOMING_SECTION = [EarlyAccessPhase.BuildUp, EarlyAccessPhase.PreEarlyAccess];

const EA_LIVE_SECTION = [EarlyAccessPhase.EarlyAccess];

const MyLoungeWrapper: FunctionComponent<Props> = ({ flashMessages }) => {
    const query = useQuery();
    const plus = useZalandoPlus();
    const isMobileScreen = useSelector(getIsMobileScreen);
    const tabs = useSelector(getTabs);
    const openCampaigns = useSelector(getOpenCampaigns);

    const recommendations = useRecommendations();
    const customer = useSelector(getCustomer);
    const recommendationsLegacy = useMemo(
        () => convertRecommendationsToLegacy(recommendations),
        [recommendations],
    );

    const tracking = useSelector(getTrackingData);
    const categoryTabId = useSelector(getCategoryTabId);
    useTrackCampaignsImpression({
        categoryTabId,
        isMobileScreen,
        visitorID: tracking?.visitorID,
    });

    useEffect(() => {
        incrementPageVisitsCount('zl_mylounge_page_view_count');
    }, []);

    useEffect(() => {
        track('view_item_list', {
            items: openCampaigns?.length,
        });
    }, []);

    const getUpcomingCampaigns = useMemo(makeGetUpcomingCampaigns, []);
    const upcomingCampaigns = useSelector((state: RootState) =>
        getUpcomingCampaigns(state, plus.isSignupEnabled),
    );

    const hasEarlyAccessCampaigns = useSelector(getHasEarlyAccessCampaigns);
    const width = useSelector(getClientDeviceWidth);
    const xsScreen = isXsScreen(width);
    const config = useConfig();
    const isPrive = config.isPriveDomain;
    const tabName = tabs?.find(t => t.id === categoryTabId)?.name;

    const [showExpiredCampaignDialog, setShowExpiredCampaignDialog] = useState(
        () =>
            !!query?.status &&
            !upcomingCampaigns.some(uc => uc.campaignId === query.event) &&
            query.event !== 'showstopper',
    );

    const expiredMessage =
        query?.status === 'upcoming' || query?.status === 'open'
            ? CampaignExpiredStatus.NOT_FOUND
            : CampaignExpiredStatus.EXPIRED;

    const handleExpiredDialogClose = useCallback(() => setShowExpiredCampaignDialog(false), []);

    const renderEarlyAccessShowstopper =
        plus.isEnabled && categoryTabId === CategoryTabIds.ALL && hasEarlyAccessCampaigns;

    const isCrossCampaignExperimentVariantOn = useVariant('ccf-desktop', 'variant_ccf_on');
    const isCrossCampaignExperimentEnabled = !!(!xsScreen && isCrossCampaignExperimentVariantOn);

    const shouldDisplayCrossCampaignFilter =
        (xsScreen || isCrossCampaignExperimentEnabled) && categoryTabId === CategoryTabIds.ALL;

    const isFilterByFreshness = useVariant(
        '9f5fdddb-a00a-4c46-90dd-549515db9661',
        'variant_CampaignFilter_on',
    );

    const showstoppers = useSelector(getShowstoppers);
    const isShowstopperEnabled = useVariant(
        '93dbf476-f988-4d84-82fd-17b086437a0c',
        'variant_getinspired_on',
    );
    const isMainTab = categoryTabId === CategoryTabIds.ALL;
    const isShowstopperAvailable = showstoppers?.length > 0;
    const shouldDisplayShowstopper = isShowstopperEnabled && isMainTab && isShowstopperAvailable;
    const showstopper = shouldDisplayShowstopper ? showstoppers[0] : undefined;
    const showstopperContainer = useMemo(
        () =>
            buildShowstopperContent(showstopper, openCampaigns, categoryTabId, tracking?.visitorID),
        [showstopper, openCampaigns],
    );
    const isNonShowstopperCampaign: (campaign: Campaign) => boolean = (campaign: Campaign) =>
        !campaign.showstopperIds.includes(showstopper.id);
    const openCampaignsToDisplay = useMemo(
        () =>
            shouldDisplayShowstopper
                ? openCampaigns.filter(isNonShowstopperCampaign)
                : openCampaigns,
        [showstopper, openCampaigns],
    );

    const shouldInjectEarlyAccessShowstopperBetweenCampaigns = useVariant(
        'a4167044-44f2-4111-8184-0fd31025b7d6',
        'daily_ea_and_position_2',
    );

    const injectInterstitialComponent = useCallback((_: Campaign, index: number) => {
        const INTERSTITIAL_INDEX = 3; /* Why 3? https://jira.zalando.net/browse/LCFAP-10719 */
        if (index === INTERSTITIAL_INDEX) {
            return <EarlyAccessSection renderWhenPhases={EA_LIVE_SECTION} />;
        }
    }, []);

    const shouldRenderEarlyAccessOnNormalPosition =
        renderEarlyAccessShowstopper &&
        ((xsScreen && !shouldInjectEarlyAccessShowstopperBetweenCampaigns) || !xsScreen);

    const isThbResizeWidthVariant = useVariant('thb_max_width', 'variant_thb_width');

    return (
        <>
            <TranslateScope path="myLounge">
                <Helmet>
                    <title>{tabName}</title>
                </Helmet>
                <S.OuterWrapper id="main">
                    <AccessibleHeader tag={Tag.H1}>
                        {isPrive ? 'ZALANDO PRIVÉ' : 'ZALANDO LOUNGE'}
                    </AccessibleHeader>
                    <ExpiredCampaignDialog
                        show={showExpiredCampaignDialog}
                        onClose={handleExpiredDialogClose}
                        showStatus={expiredMessage}
                    />
                    <Banner wrapperId={CAMPAIGN_WRAPPER} />
                    <AccessibleHeader tag={Tag.H2} aria-live="assertive">
                        {tabName || ''}
                    </AccessibleHeader>
                    {flashMessages && flashMessages.length !== 0 && (
                        <GridPageLayout>
                            <S.InlineFlashMessage>
                                {flashMessages.map((message: FlashMessageWithText) => (
                                    <InlineAlert
                                        key={message.messageId}
                                        type={convertFlashMessageToAlertType(message.type)}
                                    >
                                        {message.text || message.message}
                                    </InlineAlert>
                                ))}
                            </S.InlineFlashMessage>
                        </GridPageLayout>
                    )}
                    <UpcomingProvider>
                        {shouldDisplayCrossCampaignFilter && (
                            <CrossCampaignEntry
                                isXsScreen={xsScreen}
                                isCrossCampaignExperimentEnabled={isCrossCampaignExperimentEnabled}
                            />
                        )}
                        {xsScreen &&
                            categoryTabId === CategoryTabIds.ALL &&
                            isFilterByFreshness && <CampaignFilters />}

                        {shouldRenderEarlyAccessOnNormalPosition &&
                            (isThbResizeWidthVariant ? (
                                <GridPageLayout>
                                    <EarlyAccessSection renderWhenPhases={EA_LIVE_SECTION} />
                                </GridPageLayout>
                            ) : (
                                <EarlyAccessSection renderWhenPhases={EA_LIVE_SECTION} />
                            ))}

                        <GridPageLayout>
                            <S.Wrapper id={CAMPAIGN_WRAPPER}>
                                {xsScreen ? (
                                    <MobileCampaigns
                                        campaigns={openCampaignsToDisplay}
                                        showstopper={showstopperContainer}
                                        isPlusEnabled={plus.isEnabled}
                                        categoryTabId={categoryTabId}
                                        injectInterstitialComponent={
                                            renderEarlyAccessShowstopper &&
                                            shouldInjectEarlyAccessShowstopperBetweenCampaigns &&
                                            injectInterstitialComponent
                                        }
                                    />
                                ) : (
                                    <Grid
                                        width={width}
                                        campaigns={openCampaignsToDisplay}
                                        showstopper={showstopperContainer}
                                        isPlusEnabled={plus.isEnabled}
                                    />
                                )}
                                {!!recommendationsLegacy?.length && (
                                    <RecommendationsWrapper
                                        recommendations={recommendationsLegacy}
                                        width={width}
                                        itemWidth={RECO_ITEM_WIDTH}
                                        source={TRACK_RECO_CAMPAIGN_OVERVIEW}
                                    />
                                )}
                            </S.Wrapper>
                        </GridPageLayout>
                        <GridPageLayout>
                            <TranslateScope path="upcoming">
                                <S.UpcomingTitle preset={TitlePreset.S} tag={Tag.H2}>
                                    <Translate message="upcomingCampaigns.upcomingTitle" />
                                </S.UpcomingTitle>
                            </TranslateScope>
                        </GridPageLayout>
                        {renderEarlyAccessShowstopper &&
                            plus.isSignupEnabled &&
                            (isThbResizeWidthVariant ? (
                                <GridPageLayout>
                                    <EarlyAccessSection
                                        marginLess
                                        renderWhenPhases={EA_UPCOMING_SECTION}
                                    />
                                </GridPageLayout>
                            ) : (
                                <EarlyAccessSection
                                    marginLess
                                    renderWhenPhases={EA_UPCOMING_SECTION}
                                />
                            ))}
                        <GridPageLayout>
                            <S.Wrapper $isLastContainer id={UPCOMING_CAMPAIGNS_WRAPPER}>
                                <UpcomingSection />
                            </S.Wrapper>
                        </GridPageLayout>
                    </UpcomingProvider>
                    <BottomBubble
                        customer={
                            customer as {
                                firstName: CustomerState['firstName'];
                                lastName: CustomerState['lastName'];
                                customerNumber: CustomerState['customerNumber'];
                                email: CustomerState['email'];
                            }
                        }
                    />
                </S.OuterWrapper>
            </TranslateScope>
            {plus.member && plus.isEnabled && !plus.isSignupEnabled && <PlusWelcomeDialog />}
        </>
    );
};

export default MyLoungeWrapper;
